// Libraries
import { Flex, Grid, GridItem, Text } from '@chakra-ui/react';
// Components
import { EmissionsItem } from '../EmissionsItem';
// Utils
import { parseToThousands, parseToThousandsCurrency } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { EmissionsImpactProps } from './EmissionsImpact.types';
import { AdviceBanner } from '@/src/components/atoms/AdviceBanner/AdviceBanner';

/**
 * A component that displays information on the environmental impact of emissions.
 *
 * @component
 * @param {EmissionsImpactProps} props - Properties passed down to the component
 * @param {EmissionsImpact} props.data - The data object that contains the various emission metrics.
 *
 * @returns {ReactElement} A grid displaying emissions impact information.
 */
export const EmissionsImpact = ({ data, bulkFuelIsGreater, bulkQuantityUsedMT }: EmissionsImpactProps) => {
  const { fuelPricePerMT = 0, carbonCreditPricePerMT = 0, totalEmissionsMT = 0, mileageNM = 0 } = data ?? {};
  return (
    <Flex direction='column' gap={3.5} minH={{ base: 57, md: 62.5 }}>
      <Text color='grey.100' fontSize='s'>
        {text.cii_simulator.simulation_results_card.emissions_impact.description}
      </Text>
      <Flex direction='column' mx={2} gap={2}>
        {bulkFuelIsGreater && bulkQuantityUsedMT && (
          <AdviceBanner
            bg='#FFFFC1'
            color='#89550A'
            borderColor='ratings.C.dark'
            justifyContent='start'
            fontWeight='medium'
            gap={2}
            pl={2}
            text={`Only ${Math.round(
              bulkQuantityUsedMT
            )} MT of bulk fuel is needed to end of year. The simulation results reflect this amount.`}
          />
        )}
        <Grid
          templateColumns='repeat(2, 1fr)'
          borderRadius='xl'
          border='section'
          gap='1px'
          bg='border.100'
          overflow='hidden'
        >
          <GridItem>
            <EmissionsItem
              title={text.cii_simulator.simulation_results_card.emissions_impact.fuel_cost.title}
              tooltipText={text.cii_simulator.simulation_results_card.emissions_impact.fuel_cost.title_tooltip}
              value={fuelPricePerMT}
              formattedValue={parseToThousandsCurrency(fuelPricePerMT)}
            />
          </GridItem>
          <GridItem>
            <EmissionsItem
              title={text.cii_simulator.simulation_results_card.emissions_impact.carbon_credit.title}
              tooltipText={text.cii_simulator.simulation_results_card.emissions_impact.carbon_credit.title_tooltip}
              value={carbonCreditPricePerMT}
              formattedValue={parseToThousandsCurrency(carbonCreditPricePerMT)}
            />
          </GridItem>
          <GridItem>
            <EmissionsItem
              title={text.cii_simulator.simulation_results_card.emissions_impact.total_emissions}
              value={totalEmissionsMT}
              formattedValue={`${parseToThousands(totalEmissionsMT)} ${text.mt}`}
            />
          </GridItem>
          <GridItem>
            <EmissionsItem
              title={text.cii_simulator.simulation_results_card.emissions_impact.total_mileage}
              value={mileageNM}
              formattedValue={`${parseToThousands(mileageNM)} ${text.nm}`}
            />
          </GridItem>
        </Grid>
      </Flex>
    </Flex>
  );
};
